import "./navbar-style.component.css"

import { memo, useContext, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { StaggeringTextAnimation } from '../parallax/parallax.component'
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeContext, ThemeType } from '../../provider/ThemeProvider';

const navLinks = [
    { name: "About", path: "/#about-me" },
    { name: "Projects", path: "/#projects" },
    { name: "Contact", path: "/#contact" },
    { name: "Notes", path: "/notes" },
];

export const Navbar = memo(() => {
    const { theme, setTheme } = useContext(ThemeContext)
    const handleTheme = () => {
        if (setTheme) {
            if (theme == "dark") {
                setTheme("light")
                localStorage.theme = "light"
                document.documentElement.classList.remove('dark')
            }
            else {
                setTheme("dark")
                localStorage.theme = "dark"
                document.documentElement.classList.add('dark')
            }
        }
    }
    return (
        <>
            <div className='lg:hidden block'>
                <CustomMobileNav theme={theme} handleTheme={handleTheme} />
            </div>
            <div className='max-lg:hidden'>
                <DesktopNav theme={theme} handleTheme={handleTheme} />
            </div>
        </>
    )
})

const CustomMobileNav = ({
    modal = false,
    handleTheme,
    theme
}: {
    theme: ThemeType;
    handleTheme: () => void;
    modal?: boolean;
}) => {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const el = document.querySelector(location.hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }, [location.hash, location.pathname]);

    return (
        <nav
            className={`${modal ? "h-screen backdrop-blur-sm max-md:backdrop-blur-none" : "h-fit backdrop-blur-none"
                } z-[1000] ${theme === 'dark' ? 'text-whiteGrayish' : 'text-darkGrayish'} fixed left-1/2 top-4 w-full -translate-x-1/2 px-2`}
        >
            <div className="mt-4 flex items-center justify-center gap-1 sm:gap-2 max-w-full">
                <div className={`relative flex items-center gap-0.5 sm:gap-1 rounded-full border ${theme === 'dark' ? 'border-white/10 bg-neutral-900/70' : 'border-neutral-300/40 bg-white/70'} backdrop-blur-md px-1.5 py-1.5 sm:px-2 sm:py-2 shadow-sm overflow-x-auto max-w-full`}>
                    {navLinks.map((link) => {
                        const hasHash = link.path.includes("#");
                        const linkHash = hasHash ? link.path.slice(link.path.indexOf("#")) : "";
                        const linkPathname = hasHash ? link.path.slice(0, link.path.indexOf("#")) || "/" : link.path;

                        const isActive = hasHash
                            ? location.pathname === linkPathname && location.hash === linkHash
                            : location.pathname === link.path && !location.hash;

                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-discover="true"
                                className="relative font-syne px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap shrink-0"
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="nav-pill"
                                        initial={true}
                                        className={`absolute inset-0 z-0 rounded-full ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}
                                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                    />
                                )}                                <span
                                    className={`relative z-10 transition-colors duration-200 ${isActive
                                        ? theme === 'dark' ? 'text-black' : 'text-white'
                                        : theme === 'dark' ? 'text-whiteGrayish' : 'text-darkGrayish'
                                        }`}
                                >
                                    {link.name}
                                </span>
                            </Link>
                        );
                    })}
                </div>

                <span className="text-neutral-400 dark:text-neutral-600 select-none shrink-0">|</span>

                <button
                    onClick={handleTheme}
                    aria-label="Toggle theme"
                    className={`flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 shrink-0 rounded-full border ${theme === 'dark' ? 'border-white/10 bg-neutral-900/70' : 'border-neutral-300/40 bg-white/70'} backdrop-blur-md shadow-sm hover:scale-105 active:scale-95 transition-transform duration-200`}
                >
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                            key={theme}
                            initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                            animate={{ opacity: 1, rotate: 0, scale: 1 }}
                            exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                            transition={{ duration: 0.2 }}
                        >
                            {theme === "dark" ? (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" strokeWidth="0" stroke="currentColor" width="1em" height="1em" className="text-xl sm:text-2xl">
                                    <path strokeWidth="1.5" stroke="currentColor" className="size-4 sm:size-5" strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                                </svg>
                            ) : (
                                <svg className="size-5 sm:size-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <g>
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path d="M10 6a8 8 0 0 0 11.955 6.956C21.474 18.03 17.2 22 12 22 6.477 22 2 17.523 2 12c0-5.2 3.97-9.474 9.044-9.955A7.963 7.963 0 0 0 10 6zm-6 6a8 8 0 0 0 8 8 8.006 8.006 0 0 0 6.957-4.045c-.316.03-.636.045-.957.045-5.523 0-10-4.477-10-10 0-.321.015-.64.045-.957A8.006 8.006 0 0 0 4 12zm14.164-9.709L19 2.5v1l-.836.209a2 2 0 0 0-1.455 1.455L16.5 6h-1l-.209-.836a2 2 0 0 0-1.455-1.455L13 3.5v-1l.836-.209A2 2 0 0 0 15.29.836L15.5 0h1l.209.836a2 2 0 0 0 1.455 1.455zm5 5L24 7.5v1l-.836.209a2 2 0 0 0-1.455 1.455L21.5 11h-1l-.209-.836a2 2 0 0 0-1.455-1.455L18 8.5v-1l.836-.209a2 2 0 0 0 1.455-1.455L20.5 5h1l.209.836a2 2 0 0 0 1.455 1.455z" fillRule="nonzero" />
                                    </g>
                                </svg>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </button>
            </div>
        </nav>
    );
};

const DesktopNav = memo(({ theme, handleTheme }: { theme: ThemeType, handleTheme: () => void }) => {
    return (
        <div className="h-fit text-darkGrayish dark:text-whiteGrayish w-full z-30 absolute top-4 left-0">
            <header className='mx-auto flex max-w-screen-xl z-[1000] flex-row items-center justify-between px-4'>
                <div className='flex flex-row space-x-8 items-center'>
                    <Link to='/' className='text-base font-bold user-port-tracker tracking-wider max-lg:tracking-tight hover:opacity-90 transition'>
                        Ahmad Portfolio
                    </Link>
                </div>
                <div>
                    <div className="flex flex-row items-center text-sm font-semibold gap-x-5 max-lg:gap-x-3">
                        <div className='max-lg:hidden'>
                            <StaggeringTextAnimation href='/#contact' className='btn bg-transparent ring-0 light-glass  bg_gradient ring-none dark:ring-secondary-light ring-primary-light *:*:inline-flex *:*:gap-x-2 *:*:items-center hover:shadow-md rounded-full *:!text-white text-[14px] px-2 max-lg:p-1.5 cursor-pointer inline-flex gap-x-5 items-center'>
                                <span className='inline-block !font-black'>
                                    Let's Talk
                                </span>
                                <span className='inline-block'>
                                    <svg className='text-lg' width={"1em"} height={"1em"} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <path stroke="currentColor" strokeLinejoin="round" strokeWidth="2" d="M2.25412 8.67588L19.6606 3.59899C19.8699 3.53795 20.0958 3.59583 20.25 3.75C20.4042 3.90417 20.4621 4.13011 20.401 4.33941L15.3241 21.7459C15.2802 21.8965 15.1422 22 14.9853 22C14.8428 22 14.7143 21.9143 14.6595 21.7828L11.3167 13.7602C11.1138 13.2732 10.7268 12.8862 10.2398 12.6833L2.21719 9.3405C2.08567 9.2857 2 9.15719 2 9.01471C2 8.85784 2.10353 8.7198 2.25412 8.67588Z" />
                                        <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="M11 13L14.25 9.75" />
                                    </svg>
                                </span>
                            </StaggeringTextAnimation>
                        </div>
                        <motion.button whileTap={theme == 'dark' ? { scale: 0.89, rotate: 100, opacity: 0 } : { scale: 0.89, rotate: -100, opacity: 0 }} initial={{ opacity: 0.8 }} className='cursor-pointer max-lg:hidden active:ring-2 active:ring-offset-2 active:ring-offset-slate-50 dark:active:ring-gray-800 dark:active:ring-offset-2 ring-gray-400 p-2 rounded-full active:dark:ring-offset-2 dark:active:ring-offset-slate-700/50' onClick={handleTheme}>
                            {theme === "dark" ?
                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" strokeWidth="0" stroke="currentColor" width={"1em"} height={"1em"} className="text-2xl">
                                    <path strokeWidth="1.5" stroke="currentColor" className="size-5" strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                                </svg> :
                                <svg className='size-6' viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g><path d="M0 0h24v24H0z" fill="none" /><path d="M10 6a8 8 0 0 0 11.955 6.956C21.474 18.03 17.2 22 12 22 6.477 22 2 17.523 2 12c0-5.2 3.97-9.474 9.044-9.955A7.963 7.963 0 0 0 10 6zm-6 6a8 8 0 0 0 8 8 8.006 8.006 0 0 0 6.957-4.045c-.316.03-.636.045-.957.045-5.523 0-10-4.477-10-10 0-.321.015-.64.045-.957A8.006 8.006 0 0 0 4 12zm14.164-9.709L19 2.5v1l-.836.209a2 2 0 0 0-1.455 1.455L16.5 6h-1l-.209-.836a2 2 0 0 0-1.455-1.455L13 3.5v-1l.836-.209A2 2 0 0 0 15.29.836L15.5 0h1l.209.836a2 2 0 0 0 1.455 1.455zm5 5L24 7.5v1l-.836.209a2 2 0 0 0-1.455 1.455L21.5 11h-1l-.209-.836a2 2 0 0 0-1.455-1.455L18 8.5v-1l.836-.209a2 2 0 0 0 1.455-1.455L20.5 5h1l.209.836a2 2 0 0 0 1.455 1.455z" fillRule="nonzero" /></g></svg>
                            }
                        </motion.button>
                    </div>
                </div>
            </header>
        </div>
    )
})